import { SelectOptions, SettingsBenefit } from '@spo/logic';
import { SpoNatureFullResponse } from '@spo/types';
import { VpChevronLeftIcon, VpDeleteBinIcon } from '@vtmn-play/icons/react';
import { VpDivider, VpIconButton } from '@vtmn-play/react/headless';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Footer } from '../../../footer';
import { ProductBenefitSearchInput } from '../../../select/productBenefit';
import { UsageSegmentationSearchInput } from '../../../select/usageSegmentation';
import { CancelModal } from '../../CancelModal';
import { SegmentationSelectionInput } from '../../segmentationSelectionInput/SegmentationSelectionInput';
import { emptyBenefit } from '../DrawerNature';

type DrawerSettingsProps = {
  openExitSettingsModal: boolean;
  nature: SpoNatureFullResponse;
  selectedUsageSeg: number | null;
  productBenefits: SettingsBenefit[];
  existingUsageSeg: string;
  isFetching: boolean;
  setOpenExitSettingsModal: (value: boolean) => void;
  setOpenDrawerAddSettings: (value: boolean) => void;
  setSelectedUsageSeg: (value: number | null) => void;
  setExistingUsageSeg: (value: string) => void;
  setProductBenefits: (benefits: SettingsBenefit[]) => void;
  mutate: () => void;
};

export const limitProductBenefit = {
  min: 1,
  max: 8,
};

export const DrawerSettings = ({
  openExitSettingsModal,
  setOpenExitSettingsModal,
  setOpenDrawerAddSettings,
  nature,
  selectedUsageSeg,
  setSelectedUsageSeg,
  productBenefits,
  setProductBenefits,
  existingUsageSeg,
  setExistingUsageSeg,
  mutate,
  isFetching,
}: DrawerSettingsProps) => {
  const { t } = useTranslation();

  const exitSettings = () => {
    // Remove body overflow hidden to enable scroll on main page, due to new modal
    document.body.style.overflow = '';
    setOpenDrawerAddSettings(false);
    setOpenExitSettingsModal(false);
    setSelectedUsageSeg(null);
    setProductBenefits([emptyBenefit]);
    setExistingUsageSeg('');
  };

  const updateProductBenefits = (benefits: SettingsBenefit[]) => {
    if (
      benefits.length >= limitProductBenefit.max ||
      benefits[benefits.length - 1].benefitId === null
    ) {
      setProductBenefits(benefits);
    } else {
      setProductBenefits([
        ...benefits,
        {
          benefitId: null,
          index: benefits.length,
        },
      ]);
    }
  };

  useEffect(() => {
    if (productBenefits.length > 0) {
      updateProductBenefits(productBenefits);
    }
  }, []);

  const updateBenefit = (benefitOption: SelectOptions, index: number) => {
    const shadowBenefits = [...productBenefits];
    shadowBenefits[index] = {
      benefitId: benefitOption.value,
      index,
    };
    updateProductBenefits(shadowBenefits);
  };

  const deleteBenefit = (benefit: SettingsBenefit) => {
    const updatedBenefits = productBenefits
      .filter((b) => b.index !== benefit.index)
      .map((benefit, index) => {
        return { ...benefit, index };
      });

    if (
      updatedBenefits.length === limitProductBenefit.max - 1 &&
      updatedBenefits[updatedBenefits.length - 1].benefitId !== null
    ) {
      setProductBenefits([
        ...updatedBenefits,
        {
          benefitId: null,
          index: updatedBenefits.length,
        },
      ]);
    } else {
      setProductBenefits(updatedBenefits);
    }
  };

  return (
    <>
      <div
        data-testid="drawer-settings-container"
        className="drawer-nature-content"
      >
        <div className="drawer-header">
          <VpIconButton
            onClick={() => {
              setOpenExitSettingsModal(true);
            }}
            variant="secondary"
            size="small"
            aria-label="Go back on previous page"
            data-testid="nature-settings-go-back_link"
          >
            <VpChevronLeftIcon />
          </VpIconButton>
          <div className="drawer-title">{nature.externalReferential.name}</div>
        </div>
        <div
          className="drawer-add-settings-container"
          data-testid="drawer-add-settings-container"
        >
          <div className="drawer-add-settings-subtitle-container">
            {t('input.user-segmentation_label')}
          </div>
          <SegmentationSelectionInput
            segmentationId={nature.userSegmentationType.id}
            setSelectedSegmentationTypeId={setSelectedUsageSeg}
            disabled={true}
          />
          <div className="drawer-add-settings-subtitle-container">
            {t('input.usage-segmentation_label')}
          </div>
          <UsageSegmentationSearchInput
            usageSegmentationByIds={[]}
            existingUsageSeg={existingUsageSeg}
            selectedUsageSeg={selectedUsageSeg}
            setSelectedUsageSeg={setSelectedUsageSeg}
          />
          <VpDivider className="drawer-divider" />
          <div className="drawer-add-settings-subtitle-container">
            <span>
              {t('input.product-benefit_label')}
              <span className="product-benefit-range">
                (min: {limitProductBenefit.min} - max: {limitProductBenefit.max}
                )
              </span>
            </span>
          </div>
          <div className="drawer-product-benefit-list">
            {productBenefits.map((benefit) => (
              <div
                className="drawer-product-benefit-input"
                data-testid="drawer-product-benefit-input"
                key={benefit.index}
              >
                <ProductBenefitSearchInput
                  key={benefit.benefitId}
                  selectedProductBenefit={
                    productBenefits[benefit.index].benefitId
                  }
                  setSelectedProductBenefit={(benefitOption) =>
                    updateBenefit(benefitOption, benefit.index)
                  }
                  productBenefitList={productBenefits}
                />
                {benefit.benefitId !== null && (
                  <VpIconButton
                    className="delete-benefit-button"
                    aria-label="delete product benefit"
                    variant="tertiary"
                    size="small"
                    onClick={() => deleteBenefit(benefit)}
                    data-testid="delete-product-benefit-button"
                    disabled={productBenefits.length === 1}
                  >
                    <VpDeleteBinIcon />
                  </VpIconButton>
                )}
              </div>
            ))}
          </div>
        </div>
        <Footer
          ctaLabel={t('footer.save-and-quit-button')}
          validateAction={mutate}
          cancelAction={() => setOpenExitSettingsModal(true)}
          isValidateActionDisabled={
            productBenefits.length === 1 &&
            productBenefits[0].benefitId === null
          }
          isLoading={isFetching}
        />
      </div>
      <CancelModal
        isOpenModal={openExitSettingsModal}
        onClose={() => setOpenExitSettingsModal(false)}
        onClick={exitSettings}
      />
    </>
  );
};
